import { useQuery } from '@tanstack/react-query';
import type { PortalPaymentMethodsResponse } from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { formatCents } from '../../lib/money';
import { ErrorState, Loading } from '../../components/ui/Feedback';

/**
 * Pure presentational view of the accepted payment methods (T9.1 / §1947.3). Renders EVERY enabled
 * method from the server data — including the mandatory non-EFT option — and shows the statutory
 * "the online portal is not the exclusive way to pay; you may pay by check with no added fee"
 * copy FROM the data (`notExclusiveCopy` / `noCheckFeeCopy`), never hardcoded. Fee is shown as
 * text ("No fee" / "$X.XX fee") so the §1947.3 no-fee guarantee never depends on color alone.
 */
export function PaymentMethodsView({ data }: { data: PortalPaymentMethodsResponse }) {
  return (
    <>
      <h1>Ways to pay your rent</h1>
      <p className="subtitle">Choose whichever payment method is easiest for you.</p>

      {/* §1947.3 statutory callout — rendered from server data, not hardcoded copy. The no-fee
          guarantee only renders when the server actually resolved a fee-free non-EFT method —
          never a fabricated "pay by check with no fee" claim the estate doesn't actually offer. */}
      <div className="notice-callout" role="note" aria-labelledby="non-eft-heading">
        <h2 id="non-eft-heading" style={{ marginTop: 0 }}>
          You are never required to pay online
        </h2>
        <p style={{ margin: data.noCheckFeeCopy ? '0 0 0.5rem' : 0 }}>{data.notExclusiveCopy}</p>
        {data.noCheckFeeCopy && (
          <p style={{ margin: 0 }}>
            <strong>{data.noCheckFeeCopy}</strong>
          </p>
        )}
      </div>

      {data.paymentMethods.length === 0 ? (
        <p>No payment methods are currently configured. Please contact your property manager.</p>
      ) : (
        <ul className="grid" style={{ listStyle: 'none', padding: 0 }}>
          {data.paymentMethods.map((m) => {
            const isNonEft = !m.isEft && m.feeCents === 0;
            return (
              <li key={m.id} className="card">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <h3 style={{ margin: 0 }}>{m.label ?? m.method.replace(/_/g, ' ')}</h3>
                  {isNonEft && <span className="badge">No added fee</span>}
                </div>
                <p className="muted" style={{ margin: '0.25rem 0' }}>
                  {m.feeCents === 0 ? 'No fee' : `${formatCents(m.feeCents)} fee applies`}
                  {m.isEft ? ' · Electronic payment' : ' · Non-electronic option'}
                </p>
                {m.instructions && <p style={{ margin: '0.35rem 0' }}>{m.instructions}</p>}
                {m.remittanceTo && (
                  <p style={{ margin: '0.35rem 0' }}>
                    <span className="muted">Make payable / remit to:</span> {m.remittanceTo}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

/** Container: fetches the enabled methods + §1947.3 copy and renders the view. */
export function PaymentMethods() {
  const query = useQuery({
    queryKey: ['portal', 'payment-methods'],
    queryFn: () => api.portal.paymentMethods(),
  });

  if (query.isLoading) return <Loading label="Loading payment methods…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;
  return <PaymentMethodsView data={query.data} />;
}
